import { memo, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useSQuadrantStyles } from './style';
import { BarIcon, SidebarCollapseIcon, SidebarExpandIcon } from '../sicons';
import SText from '../sui/stext';
import { SRoundedContainer } from '../sui/srounded-container';

/**
 * Widest viewport that still offers the collapse toggle. Above it the 320px
 * panel and the results both have room, so the control would be noise.
 *
 * Because the toggle isn't rendered above this width, crossing back up has to
 * force the panel open again — otherwise a panel collapsed on a narrow window
 * would stay shut after maximising, with no control left to reopen it.
 */
const COLLAPSIBLE_MAX_WIDTH = 1380;

interface TopContent {
  title?: string;
  content?: JSX.Element;
}

/**
 * Passed to a `header` render function so the header can host the side panel's
 * collapse toggle. The layout owns the state; the header only decides where the
 * button goes, because only it knows what the button should sit beside.
 */
export interface SQuadrantHeaderControls {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  /** Ready-made toggle — render it wherever it belongs in the header. */
  collapseToggle: JSX.Element | null;
}

interface SQuadrantLayoutProps {
  defaultCollapsed?: boolean;
  showCollapseButton?: boolean;
  invertedRow?: boolean;
  topLeft?: TopContent;
  topRight?: TopContent;
  bottomLeft: JSX.Element;
  bottomRight: JSX.Element;
  /**
   * A plain element, or a function given {@link SQuadrantHeaderControls} when
   * the header wants to place the collapse toggle itself.
   */
  header?: JSX.Element | ((controls: SQuadrantHeaderControls) => JSX.Element);
}

const SQuadrantLayout = ({
  defaultCollapsed = false,
  showCollapseButton = true,
  invertedRow = false,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  header
}: SQuadrantLayoutProps) => {
  const classes = useSQuadrantStyles();
  const theme = useTheme();
  // `noSsr` on both so the first paint already knows the viewport, instead of
  // flashing the panel open and then shutting it.
  const canCollapse = useMediaQuery(
    theme.breakpoints.down(COLLAPSIBLE_MAX_WIDTH),
    { noSsr: true }
  );
  // The side panel is a fixed 320px, which on a tablet is most of the usable
  // width and leaves almost nothing for the results next to it — so there it
  // also *starts* closed rather than merely being closable (DEV-726).
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'), {
    noSsr: true
  });
  const [isCollapsed, setIsCollapsed] = useState(
    defaultCollapsed || isTabletDown
  );

  useEffect(() => {
    // Above the collapsible width the toggle is gone, so the panel must never
    // be left shut — only the host's own `defaultCollapsed` still applies, and
    // that one clears itself.
    setIsCollapsed(defaultCollapsed || (canCollapse && isTabletDown));
  }, [defaultCollapsed, canCollapse, isTabletDown]);

  // The panel holds both the Categories tree and the Filters, so the label
  // names both — "Filters" alone undersold what the button reveals.
  const collapseLabel = isCollapsed
    ? 'Show Categories & Filters'
    : 'Hide Categories & Filters';
  const toggleCollapsed = () => setIsCollapsed(collapsed => !collapsed);

  // Panel glyph with a chevron in its content area: pointing into the side
  // column while the panel is open (fold it away), out of it while collapsed
  // (pull it back out) — two distinct icons for the two states.
  const PanelToggleIcon = isCollapsed ? SidebarExpandIcon : SidebarCollapseIcon;

  // Only built on a viewport narrow enough to need it, where there is a panel
  // to fold and the host wants the affordance; the header decides where it
  // actually gets rendered. An icon button so it stays compact in toolbars
  // that are tight by definition here, with a tooltip naming both panel
  // sections.
  const collapseToggle =
    canCollapse && showCollapseButton && !!bottomLeft ? (
      <Tooltip title={collapseLabel} placement="bottom" arrow>
        <IconButton
          size="small"
          aria-label={collapseLabel}
          aria-expanded={!isCollapsed}
          className={classes.collapseButton}
          onClick={toggleCollapsed}>
          <PanelToggleIcon
            style={{
              fontSize: 18,
              // A right-hand panel mirrors the glyph so the drawn side
              // column sits on the side the real panel is on.
              transform: invertedRow ? 'scaleX(-1)' : undefined
            }}
          />
        </IconButton>
      </Tooltip>
    ) : null;

  return (
    <Box className={classes.container}>
      {typeof header === 'function'
        ? header({ isCollapsed, toggleCollapsed, collapseToggle })
        : header}
      <Box className={classes.grid}>
        {!isCollapsed && (
          <Box
            style={{ maxWidth: 320, minWidth: 320 }}
            className={clsx({
              [classes.left]: !invertedRow,
              [classes.right]: invertedRow,
              [classes.order2]: invertedRow,
              [classes.gridItem]: true
            })}>
            {topLeft && (
              <Box className={classes.topConatiner}>
                {topLeft?.title && (
                  <SText weight="bold" title={topLeft.title} />
                )}
                {topLeft?.content || null}
              </Box>
            )}
            {!!bottomLeft && (
              <SRoundedContainer className={classes.bottomContainer}>
                {bottomLeft}
              </SRoundedContainer>
            )}
          </Box>
        )}

        <Box className={classes.gridItem}>
          {topRight && (
            <Box className={classes.topConatiner}>
              {showCollapseButton && (
                <IconButton onClick={toggleCollapsed}>
                  <BarIcon />
                </IconButton>
              )}
              {topRight?.title && (
                <SText weight="bold" title={topRight.title} />
              )}
              {topRight?.content || null}
            </Box>
          )}
          <SRoundedContainer className={classes.bottomContainer}>
            {bottomRight}
          </SRoundedContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(SQuadrantLayout);
